import { api } from './api';

const TYPE_LABELS = {
  vm: 'Virtual machine', storage: 'Storage', database: 'Database',
  network: 'Network', load_balancer: 'Load balancer',
};

const FALLBACK_FIELDS = {
  aws: { vm: [
    { name: 'image_id', label: 'AMI ID', required: true, placeholder: 'ami-0123456789abcdef0', pattern: '^ami-[A-Za-z0-9]+$', hint: 'Use an AMI available in the selected region.' },
    { name: 'instance_type', label: 'Instance type', required: true, placeholder: 't3.micro', pattern: '^[A-Za-z0-9][A-Za-z0-9._-]+$' },
  ] },
  gcp: { vm: [
    { name: 'zone', label: 'Zone', required: true, placeholder: 'us-central1-a', pattern: '^[a-z]+-[a-z0-9]+[0-9]-[a-z]$' },
    { name: 'machine_type', label: 'Machine type', required: true, placeholder: 'e2-micro', pattern: '^[a-z][a-z0-9-]+$' },
    { name: 'image_family', label: 'Image family', required: true, placeholder: 'debian-12', pattern: '^[A-Za-z0-9][A-Za-z0-9._-]+$' },
    { name: 'image_project', label: 'Image project', required: true, placeholder: 'debian-cloud', pattern: '^[A-Za-z0-9][A-Za-z0-9._-]+$' },
    { name: 'disk_size_gb', label: 'Boot disk size', suffix: 'GB', type: 'number', min: 1, required: true, placeholder: '20' },
  ] },
  oracle: { vm: [
    { name: 'availability_domain', label: 'Availability domain', required: true, placeholder: 'Your tenancy availability domain' },
    { name: 'image_id', label: 'Image OCID', required: true, placeholder: 'ocid1.image…', pattern: '^ocid1\\.image\\.' },
    { name: 'subnet_id', label: 'Subnet OCID', required: true, placeholder: 'ocid1.subnet…', pattern: '^ocid1\\.subnet\\.' },
    { name: 'shape', label: 'Shape', required: true, placeholder: 'VM.Standard.E4.Flex' },
  ] },
  contabo: {
    vm: [
      { name: 'image_id', label: 'Image ID', required: true, placeholder: 'Provider image UUID' },
      { name: 'product_id', label: 'Product ID', required: true, placeholder: 'V92' },
    ],
    storage: [{ name: 'size_gb', label: 'Purchased space', suffix: 'GB', type: 'number', min: 1, required: true, placeholder: '250' }],
  },
};

const asType = (entry) => typeof entry === 'string' ? { id: entry } : { ...entry, id: entry.id || entry.resource_type || entry.type || entry.value };
const humanize = (value) => String(value || '').replace(/_/g, ' ').replace(/^./, (character) => character.toUpperCase());
const disabledReason = (reason) => ({
  provider_create_capability_unavailable: 'This adapter cannot safely create resources with its current capability settings.',
}[reason] || reason || 'Provisioning is not available for this provider.');

export const normalizeProvisioningProvider = (provider = {}) => {
  const provisioning = provider.provisioning || {};
  const governed = provider.governed_catalog || provisioning.catalog;
  const rawTypes = governed?.resource_types || provisioning.types || provisioning.resource_types || [];
  const types = (Array.isArray(rawTypes) ? rawTypes : Object.entries(rawTypes).map(([id, value]) => ({ id, ...(value || {}) })))
    .map(asType)
    .filter((entry) => entry.id)
    .map((entry) => {
      const detail = governed?.[entry.id] || governed?.types?.[entry.id] || entry;
      const estimate = detail.pricing || detail.cost_estimate || detail.estimate || provisioning.cost_estimates?.[entry.id] || null;
      const location = detail.location || {};
      return {
        id: entry.id,
        label: detail.label || TYPE_LABELS[entry.id] || entry.id,
        enabled: detail.can_create !== false,
        fields: (detail.fields || FALLBACK_FIELDS[provider.type]?.[entry.id] || []).map((field) => ({
          ...field,
          label: field.label || humanize(field.name),
          type: field.type === 'integer' ? 'number' : field.type,
          min: field.minimum ?? field.min,
          max: field.maximum ?? field.max,
        })),
        regions: location.options || detail.regions || provisioning.regions || [],
        location_pattern: location.pattern,
        zones: detail.zones || [],
        estimate: estimate?.available !== false && Number.isFinite(Number(estimate?.amount)) && estimate.source
          ? { amount: Number(estimate.amount), currency: /^[A-Z]{3}$/.test(estimate.currency || '') ? estimate.currency : 'USD', interval: estimate.interval || 'month', source: estimate.source, as_of: estimate.as_of }
          : null,
      };
    });
  const enabled = governed ? governed.can_create === true && types.some((type) => type.enabled) : provisioning.enabled === true && types.length > 0;
  return {
    id: provider.id, name: provider.name, provider_type: provider.type,
    enabled, types,
    disabled_reason: enabled ? null : provider.catalog_error || (governed
      ? disabledReason(governed.disabled_reason)
      : provisioning.disabled_reason || (provider.capabilities?.create === false ? 'This adapter does not support safe provisioning.' : 'Provisioning is not available for this provider.')),
  };
};

export const normalizeProvisioningProviders = (providers = []) => providers.map(normalizeProvisioningProvider);

export const deriveRegion = (providerType, region, config) => {
  if (providerType !== 'gcp') return String(region || '').trim();
  const zone = String(config?.zone || '').trim();
  return zone.includes('-') ? zone.split('-').slice(0, -1).join('-') : zone;
};

export const validateProvisioningDraft = (draft, provider, type) => {
  const errors = {};
  if (!provider?.enabled) errors.provider_id = provider?.disabled_reason || 'Choose a supported provider.';
  if (!type?.enabled) errors.resource_type = 'Choose a catalog-supported resource type.';
  const name = String(draft.name || '').trim();
  if (!name) errors.name = 'Give this resource a name.';
  else if (!/^[A-Za-z0-9][A-Za-z0-9._ -]{0,62}$/.test(name)) errors.name = 'Start with a letter or number and use no more than 63 letters, numbers, spaces, dots, underscores, or hyphens.';
  const region = deriveRegion(provider?.provider_type, draft.region, draft.config);
  if (!region) errors.region = provider?.provider_type === 'gcp' ? 'Enter a valid zone.' : 'Choose or enter a region.';
  if (region && type?.location_pattern) {
    try { if (!new RegExp(type.location_pattern).test(region)) errors.region = 'Location has an invalid format for this provider.'; }
    catch (_) { errors.region = 'Location validation is unavailable; ask an administrator to repair the catalog.'; }
  }
  if (region && type?.regions?.length && !type.regions.some((option) => (option.value || option) === region)) errors.region = 'Choose a location offered by this provider.';
  (type?.fields || []).forEach((field) => {
    const raw = draft.config?.[field.name];
    const empty = raw === undefined || raw === null || String(raw).trim() === '';
    if (field.required && empty) errors[field.name] = `${field.label} is required.`;
    if (!empty && field.type === 'number' && (!Number.isInteger(Number(raw)) || Number(raw) < (field.min ?? 0) || (field.max !== undefined && Number(raw) > field.max))) {
      errors[field.name] = `${field.label} must be a whole number${field.min !== undefined ? ` from ${field.min}` : ''}${field.max !== undefined ? ` to ${field.max}` : ''}.`;
    }
    if (!empty && field.options?.length && !field.options.some((option) => (option.value || option) === raw)) {
      errors[field.name] = `Choose a supported ${field.label.toLowerCase()}.`;
    }
    if (!empty && field.pattern) {
      try { if (!new RegExp(field.pattern).test(String(raw))) errors[field.name] = `${field.label} has an invalid format.`; }
      catch (_) { errors[field.name] = `${field.label} validation is unavailable; ask an administrator to repair the catalog.`; }
    }
  });
  return errors;
};

export const buildProvisioningSnapshot = (draft, provider, type) => ({
  provider_id: provider.id,
  provider_name: provider.name,
  provider_type: provider.provider_type,
  resource_type: type.id,
  resource_type_label: type.label,
  name: String(draft.name).trim(),
  region: deriveRegion(provider.provider_type, draft.region, draft.config),
  config: Object.fromEntries((type.fields || []).filter((field) => draft.config[field.name] !== undefined && String(draft.config[field.name]).trim() !== '').map((field) => [field.name, field.type === 'number' ? Number(draft.config[field.name]) : String(draft.config[field.name]).trim()])),
  estimate: type.estimate ? { ...type.estimate } : null,
});

export const provisioningRequestPayload = (snapshot) => ({
  spec: { name: snapshot.name, resource_type: snapshot.resource_type, location: snapshot.region, config: snapshot.config },
  pricing_unavailable_acknowledged: snapshot.estimate === null,
});

export const newProvisioningIntentKey = () => window.crypto?.randomUUID?.() || `provision-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const basePath = ({ organizationId, projectId }) => `/v1/organizations/${encodeURIComponent(organizationId)}/projects/${encodeURIComponent(projectId)}`;
export const provisioningCatalogPath = (scope, providerId) => `${basePath(scope)}/providers/${encodeURIComponent(providerId)}/provisioning-catalog`;
export const provisioningRequestPath = (scope, providerId) => `${basePath(scope)}/providers/${encodeURIComponent(providerId)}/provisioning-requests`;

export const provisioningApi = {
  catalog: async (scope) => {
    const providerResponse = await api.getProviders();
    const providers = providerResponse.data.providers || providerResponse.data || [];
    const enriched = await Promise.all(providers.map(async (provider) => {
      try {
        const response = await api.get(provisioningCatalogPath(scope, provider.id));
        return { ...provider, governed_catalog: response.data.catalog };
      } catch (error) {
        return { ...provider, governed_catalog: { can_create: false, resource_types: [] }, catalog_error: error?.response?.status === 403 ? 'You do not have permission to request provisioning from this provider.' : 'The governed provisioning catalog could not be verified.' };
      }
    }));
    return { data: { providers: enriched } };
  },
  createAndSubmit: async (scope, snapshot, idempotencyKey) => {
    const response = await api.post(provisioningRequestPath(scope, snapshot.provider_id), provisioningRequestPayload(snapshot), { headers: { 'Idempotency-Key': idempotencyKey } });
    return response.data;
  },
};
